import { useEffect, useState } from 'react';
import { addSeconds, chipTime } from '../util/mcpbTime';

const POLL_MS = 250; // clock granularity is whole seconds; poll a bit finer so the shown second stays crisp

/**
 * useCellClock — the per-cell OWN playback timestamp for the MCPlayback overlay.
 *
 * Each streaming cell shows the actual wall-clock time of the frame IT is displaying —
 * the active clip's start (`activeClip.sTimestamp`, its own basis) PLUS the player's REAL
 * in-clip position (`PlaybackPlayer.playheadSec` for the WebCodecs/canvas path,
 * `<video>.currentTime` for local + cloud-legacy) — NOT the shared master-playhead time.
 *
 * WHY (David, all product modes): a single shared timestamp on every cell MASKS desync — the
 * grid can look synced (identical time in every corner) even while one cam plays ahead of another.
 * The cell's own clock is both the honest display AND the diagnostic that proves the multi-cam
 * seek-sync gate: when two cams hold-then-play together their clocks match; when they don't, the
 * clocks diverge visibly.
 *
 * Mirrors useVideoInfo / useCanvasInfo: a light poll (does NOT re-render per frame), and it only
 * pushes state when the displayed HH:MM:SS actually changes (≤1/s).
 *
 * Gap behavior (David 2026-08-05): a cell that drops into a gap must NOT keep ticking the shared master
 * clock over dead air — that reads as if the cam were still playing. Instead the clock FREEZES at the
 * last frame's own-time (paired with the frozen last-frame image the cell paints), and stays there until
 * footage returns. A cell that never showed a frame reads blank (the placeholder is the "no footage"
 * cue). So we simply STOP polling in a gap and leave `ts` at its last value — never advancing it.
 *
 * @param {function} getPosition returns the active player's current in-clip seconds (video.currentTime
 *                               or PlaybackPlayer.playheadSec), or null/NaN when unavailable
 * @param {object} opts
 * @param {string} opts.clipStartTs  the active clip's start 'YYYYMMDDHHMMSS' (its OWN time basis)
 * @param {boolean} opts.hasClip     a clip is loaded under the playhead for this cell
 * @returns {string} the HH:MM:SS to display in this cell's timestamp overlay ('' before any frame)
 */
export const useCellClock = (getPosition, { clipStartTs = '', hasClip = false } = {}) => {
  const [ts, setTs] = useState('');

  useEffect(() => {
    // No clip under the playhead (a gap) → FREEZE: leave `ts` at its last own-time; do not advance it.
    if (!hasClip || !clipStartTs || typeof getPosition !== 'function') return undefined;
    let cancelled = false;
    const commit = () => {
      if (cancelled) return;
      const pos = getPosition();
      const secs = Number.isFinite(pos) && pos > 0 ? pos : 0;
      const next = chipTime(addSeconds(clipStartTs, secs));
      setTs((prev) => (prev === next ? prev : next)); // only re-render when the shown second changes
    };
    commit();                                  // immediate first snapshot (position at mount / after a seek)
    const timer = setInterval(commit, POLL_MS);
    return () => { cancelled = true; clearInterval(timer); };
  }, [getPosition, clipStartTs, hasClip]);

  return ts;
};

export default useCellClock;
